import React from 'react';
import { useTranslation } from 'react-i18next';

import { Conversation, ConversationNote, ConversationNoteVisibility } from '../types/Conversation';
import { formatDateTime } from '../../../shared/utils/localization';
import detailsStyles from './ConversationDetails.module.css';

interface ConversationNotesModalProps {
  conversation: Conversation;
  currentEmployeeId?: string;
  isOpen: boolean;
  isLoading?: boolean;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onCreateNote: (visibility: ConversationNoteVisibility, text: string) => Promise<void>;
  onUpdateNote: (noteId: string, text: string) => Promise<void>;
  onDeleteNote: (noteId: string) => Promise<void>;
}

type ComposerState =
  | { mode: 'create'; visibility: ConversationNoteVisibility }
  | { mode: 'edit'; noteId: string };

const ConversationNotesModal: React.FC<ConversationNotesModalProps> = ({
  conversation,
  currentEmployeeId,
  isOpen,
  isLoading = false,
  isSaving,
  error,
  onClose,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
}) => {
  const { t } = useTranslation(['conversations', 'common']);
  const [isChooserOpen, setIsChooserOpen] = React.useState(false);
  const [composer, setComposer] = React.useState<ComposerState | null>(null);
  const [draft, setDraft] = React.useState('');

  const formatNoteTimestamp = React.useCallback((timestamp: string) => {
    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) {
      return t('unknownTime', { ns: 'conversations' });
    }

    return formatDateTime(timestamp);
  }, [t]);

  const getVisibilityLabel = React.useCallback((visibility: ConversationNoteVisibility) => (
    visibility === 'public'
      ? t('public', { ns: 'common' })
      : t('private', { ns: 'common' })
  ), [t]);

  React.useEffect(() => {
    if (!isOpen) {
      setIsChooserOpen(false);
      setComposer(null);
      setDraft('');
    }
  }, [isOpen]);

  React.useEffect(() => {
    setIsChooserOpen(false);
    setComposer(null);
    setDraft('');
  }, [conversation.id]);

  const openCreateComposer = React.useCallback((visibility: ConversationNoteVisibility) => {
    setComposer({ mode: 'create', visibility });
    setDraft('');
    setIsChooserOpen(false);
  }, []);

  const openEditComposer = React.useCallback((note: ConversationNote) => {
    setComposer({ mode: 'edit', noteId: note.id });
    setDraft(note.text);
    setIsChooserOpen(false);
  }, []);

  const cancelComposer = React.useCallback(() => {
    setComposer(null);
    setDraft('');
  }, []);

  const submitComposer = React.useCallback(async () => {
    const text = draft.trim();
    if (!text || isSaving || !composer) {
      return;
    }

    if (composer.mode === 'create') {
      await onCreateNote(composer.visibility, text);
    } else {
      await onUpdateNote(composer.noteId, text);
    }

    setComposer(null);
    setDraft('');
  }, [composer, draft, isSaving, onCreateNote, onUpdateNote]);

  const handleDelete = React.useCallback(async (note: ConversationNote) => {
    if (isSaving) {
      return;
    }

    const confirmed = window.confirm(t('deleteNoteConfirm', { ns: 'conversations' }));
    if (!confirmed) {
      return;
    }

    await onDeleteNote(note.id);
  }, [isSaving, onDeleteNote, t]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={detailsStyles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('conversationNotes', { ns: 'conversations' })}>
      <div className={`${detailsStyles.modalCard} ${detailsStyles.notesModalCard}`}>
        <div className={detailsStyles.modalHeader}>
          <div>
            <div className={detailsStyles.modalTitle}>{t('conversationNotes', { ns: 'conversations' })}</div>
            <div className={detailsStyles.notesModalSubtitle}>
              {conversation.visibleNoteCount > 0
                ? t('visibleNotesCount', { ns: 'conversations', count: conversation.visibleNoteCount })
                : t('noNotesYetShort', { ns: 'conversations' })}
            </div>
          </div>
          <div className={detailsStyles.notesModalHeaderActions}>
            <div className={detailsStyles.notesCreateWrap}>
              <button
                type="button"
                className={detailsStyles.modalPrimaryBtn}
                onClick={() => setIsChooserOpen(current => !current)}
                disabled={isSaving || isLoading}
              >
                {t('addNote', { ns: 'conversations' })}
              </button>
              {isChooserOpen && (
                <div className={detailsStyles.notesCreateMenu}>
                  <button
                    type="button"
                    className={detailsStyles.notesCreateMenuBtn}
                    onClick={() => openCreateComposer('private')}
                    disabled={isSaving}
                  >
                    {t('private', { ns: 'common' })}
                  </button>
                  <button
                    type="button"
                    className={detailsStyles.notesCreateMenuBtn}
                    onClick={() => openCreateComposer('public')}
                    disabled={isSaving}
                  >
                    {t('public', { ns: 'common' })}
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              className={detailsStyles.modalCloseBtn}
              onClick={onClose}
              disabled={isSaving}
            >
              {t('close', { ns: 'common' })}
            </button>
          </div>
        </div>

        {(composer || error) && (
          <div className={detailsStyles.notesComposerSection}>
            {composer && (
              <>
                <div className={detailsStyles.notesComposerHeader}>
                  <div className={detailsStyles.notesComposerTitle}>
                    {composer.mode === 'create'
                      ? t('newVisibilityNote', { ns: 'conversations', visibility: getVisibilityLabel(composer.visibility) })
                      : t('editNote', { ns: 'conversations' })}
                  </div>
                  <button
                    type="button"
                    className={detailsStyles.modalSecondaryBtn}
                    onClick={cancelComposer}
                    disabled={isSaving}
                  >
                    {t('cancel', { ns: 'common' })}
                  </button>
                </div>
                <textarea
                  value={draft}
                  onChange={event => setDraft(event.target.value)}
                  className={detailsStyles.notesComposerInput}
                  placeholder={t('noteComposerPlaceholder', { ns: 'conversations' })}
                  rows={4}
                  maxLength={2000}
                  disabled={isSaving}
                />
                <div className={detailsStyles.notesComposerFooter}>
                  <span className={detailsStyles.notesComposerCount}>{draft.trim().length}/2000</span>
                  <button
                    type="button"
                    className={detailsStyles.modalPrimaryBtn}
                    onClick={() => void submitComposer()}
                    disabled={isSaving || !draft.trim()}
                  >
                    {isSaving
                      ? t('saving', { ns: 'common' })
                      : composer.mode === 'create'
                        ? t('createNote', { ns: 'conversations' })
                        : t('saveChanges', { ns: 'conversations' })}
                  </button>
                </div>
              </>
            )}
            {error && <div className={detailsStyles.modalError}>{error}</div>}
          </div>
        )}

        <div className={detailsStyles.modalList}>
          {isLoading ? (
            <div className={detailsStyles.notesLoadingState}>{t('loadingNotes', { ns: 'conversations', defaultValue: 'Loading notes...' })}</div>
          ) : conversation.notes.length === 0 ? (
            <div className={detailsStyles.modalHint}>{t('noNotesAvailableForConversation', { ns: 'conversations' })}</div>
          ) : (
            conversation.notes.map(note => {
              const isEdited = note.updatedAt !== note.createdAt;
              const canManageNote = Boolean(currentEmployeeId && note.createdByEmployeeId === currentEmployeeId);
              return (
                <div key={note.id} className={detailsStyles.noteListItem}>
                  <div className={detailsStyles.noteListMetaRow}>
                    <span className={`${detailsStyles.noteVisibilityBadge} ${note.visibility === 'public' ? detailsStyles.noteVisibilityPublic : detailsStyles.noteVisibilityPrivate}`}>
                      {getVisibilityLabel(note.visibility)}
                    </span>
                    <span className={detailsStyles.noteListAuthor}>{note.createdByEmployeeName || t('unknownAuthor', { ns: 'common' })}</span>
                    <span className={detailsStyles.noteListTimestamp}>
                      {isEdited
                        ? t('updatedAt', { ns: 'conversations', timestamp: formatNoteTimestamp(note.updatedAt) })
                        : formatNoteTimestamp(note.createdAt)}
                    </span>
                  </div>
                  <div className={detailsStyles.noteListBody}>{note.text}</div>
                  {canManageNote && (
                    <div className={detailsStyles.noteListActions}>
                      <button
                        type="button"
                        className={detailsStyles.noteListActionBtn}
                        onClick={() => openEditComposer(note)}
                        disabled={isSaving}
                      >
                        {t('edit', { ns: 'common' })}
                      </button>
                      <button
                        type="button"
                        className={`${detailsStyles.noteListActionBtn} ${detailsStyles.noteListDeleteBtn}`}
                        onClick={() => void handleDelete(note)}
                        disabled={isSaving}
                      >
                        {t('delete', { ns: 'common' })}
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationNotesModal;